// Ambient audio — the client-side Web Audio mixer (CLIENT-ONLY: nothing under
// src/server/, src/sim/, src/map/, or the world-build chain may import this).
//
// The model is map-doc data (see content/SCHEMA.md "ambience + audioZones"):
// a map's `ambience` names a BASE loop that plays everywhere on it, and each
// `audioZones[]` disc fades its track in by proximity — full volume inside
// `inner`, silent at/past `r`, smoothstepped between. Zone layers are ADDITIVE
// over the base (a river over the field); `music: true` zones (a town's
// soundtrack) instead DUCK the base out in proportion to their own fade, so
// walking into town crossfades field → music and walking out reverses it.
//
// Weather rain beds sit ON TOP of that model (global, not position-faded):
// `setWeatherAmbience` drives looped `rain-light` / `rain-heavy` layers and a
// map-ambience duck under heavy rain. Driven each frame from weather.ts —
// tracks fetch/decode ONCE (page-lifetime buffer cache), same as every other
// bed; weather wire msgs never carry audio bytes.
//
// Mechanics worth knowing before editing:
// - Tracks are plain files fetched from /audio/<track>.<ext> (assets/audio/
//   on disk) — .opus preferred, .mp3 the fallback (AUDIO_EXTS) — decoded ONCE
//   into AudioBuffers and cached for the page lifetime. A missing/undecodable
//   track console.warns and plays silence — never throws.
// - Browsers gate AudioContexts on a user gesture: initAudio registers a
//   one-time pointerdown+keydown pair that resume()s the context INSIDE the
//   gesture handler (Safari requires the synchronous call). Until then the
//   graph runs silently — no special casing anywhere else.
// - AudioBufferSourceNodes are one-shot: every (re)start builds a fresh looped
//   source, started at a random offset so overlapping zones don't phase-lock.
//   A layer whose gain has faded to ~0 stops its source after a short hold
//   (restart-thrash guard at a fade edge) — fades are gain-driven, dt-eased in
//   updateAudio on the rAF loop (no timers, matching the client convention).
// - Volumes (master/music/ambience/mute) persist in localStorage and edit from
//   sliders this module appends to the O-key settings panel (#gfx-panel), the
//   game.ts "Switch character" extension precedent.
// - One-shot SFX ride a pre-wired sfxGain bus under master: playSfx(name)
//   decode-caches the same way as ambience and fires an unlooped source into
//   it. Overlapping plays are free (each shot is its own BufferSource).

import { game } from "./state.ts";
import { smoothstep } from "./daynight.ts";
import type { MapDoc, MapAudioZone } from "./content/schemas.ts";

// ---- Mixer state --------------------------------------------------------------

let ctx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let ambienceGain: GainNode | null = null;
let musicGain: GainNode | null = null;
let sfxGain: GainNode | null = null; // one-shot SFX bus (playSfx)
let unlocked = false;

/** Decoded-track cache — page-lifetime, survives map swaps. */
const buffers = new Map<string, AudioBuffer | "loading" | "missing">();

type Layer = {
  track: string;
  gain: GainNode; // → ambienceGain or musicGain
  src: AudioBufferSourceNode | null;
  cur: number; // eased gain value
  hold: number; // seconds of near-silence left before the source stops
};
/** Live layers keyed `a:<track>` / `m:<track>` / `w:<track>` — zones sharing a
 *  track share a layer (loudest zone wins), and base + zone on the SAME track
 *  do too. `w:` is weather-only (never collides with map ambience keys). */
const layers = new Map<string, Layer>();

/** The active map's audio data (set by setAudioMap — initWorld for the live
 *  world, buildCineStage for a cinematic stage). */
let baseTrack: string | null = null;
let zones: MapAudioZone[] = [];

/** Ephemeral proximity ambience (live campfires, etc.) — same fade math as
 *  map `audioZones`, additive over the base. Not map-doc data; callers replace
 *  the whole list when sources change. Cleared by resetAudio / setAudioMap(null). */
export type AmbientEmitter = {
  track: string;
  x: number;
  z: number;
  /** Full silence at/past this radius. */
  r: number;
  /** Full volume inside this radius (must be ≤ r). */
  inner: number;
  /** 0..1 trim on the layer (soft beds use ~0.35). */
  volume?: number;
};
let emitters: AmbientEmitter[] = [];

/** Global weather rain beds (not position-faded). Set each frame by weather.ts;
 *  rides the ambience bus under a dedicated `w:` key prefix. Map swap does NOT
 *  wipe them; weatherClear / snow / shelter zero targets (weatherClear also
 *  snaps zeros immediately so beds start fading without waiting a frame). */
const WEATHER_LIGHT = "rain-light";
const WEATHER_HEAVY = "rain-heavy";
const WEATHER_LIGHT_KEY = "w:" + WEATHER_LIGHT;
const WEATHER_HEAVY_KEY = "w:" + WEATHER_HEAVY;
/** At full music-zone presence, rain beds keep this fraction (town soundtrack
 *  still owns the mix; rain remains weather color without stacking full-on). */
const WEATHER_MUSIC_TRIM = 0.65;
let weatherLight = 0;
let weatherHeavy = 0;
/** 0 = full map ambience, 1 = silence map base/zones/emitters (music untouched). */
let ambienceDuck = 0;

/** Listener-position override. On a cinematic stage the CAMERA is what travels
 *  (game.player idles off-scene at its pre-cinematic spot), so zone distances
 *  follow it — the same anchor updateDecoView uses. Null = the player. */
let anchor: { position: { x: number; z: number } } | null = null;
export function setAudioAnchor(a: { position: { x: number; z: number } } | null): void {
  anchor = a;
}

/** Per-frame scratch for updateAudio's layer targets — module-scope reuse, the
 *  hot-path convention (weather.ts _p/_s), never retained across frames. */
const want = new Map<string, number>();

const FADE_TAU = 0.8; // seconds — exponential-approach time constant
const STOP_HOLD = 2; // seconds a silent layer keeps its source warm

// ---- Volume settings (persisted) ----------------------------------------------

type Vols = { master: number; music: number; ambience: number; muted: boolean };
const VOL_KEY = "audioVol";

function loadVols(): Vols {
  try {
    const v = JSON.parse(localStorage.getItem(VOL_KEY) ?? "");
    return {
      master: clamp01(v.master), music: clamp01(v.music),
      ambience: clamp01(v.ambience), muted: v.muted === true,
    };
  } catch { /* absent / malformed — defaults */ }
  return { master: 1, music: 1, ambience: 1, muted: false };
}
const vols: Vols = loadVols();

function clamp01(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.min(Math.max(v, 0), 1) : 1;
}

function saveVols(): void {
  try { localStorage.setItem(VOL_KEY, JSON.stringify(vols)); } catch { /* ignore */ }
}

// ---- Public API ----------------------------------------------------------------

/** Boot the mixer + the one-time gesture unlock + the settings-panel sliders.
 *  Call once, AFTER initSettingsUI (the sliders append to #gfx-panel). */
export function initAudio(): void {
  try {
    ctx = new AudioContext();
  } catch (e) {
    console.warn("audio: AudioContext unavailable — sound disabled", e);
    return;
  }
  masterGain = ctx.createGain();
  masterGain.connect(ctx.destination);
  ambienceGain = ctx.createGain();
  ambienceGain.connect(masterGain);
  musicGain = ctx.createGain();
  musicGain.connect(masterGain);
  sfxGain = ctx.createGain();
  sfxGain.connect(masterGain);

  // Autoplay unlock: resume() must run synchronously inside a real gesture.
  const unlock = () => {
    unlocked = true;
    void ctx!.resume().catch(() => {});
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);

  appendVolumeSliders();
}

/** Point the mixer at a map's audio data (initWorld) — kicks track fetches so
 *  the loops are usually decoded before the fade-in wants them. Layers from
 *  the previous map fade out naturally (their zones stop matching). */
export function setAudioMap(doc: MapDoc | null): void {
  baseTrack = doc?.ambience ?? null;
  zones = doc?.audioZones ?? [];
  // Map swap / teardown clears ephemeral emitters too — live fires re-register
  // via setFires after the held message queue replays.
  if (!doc) emitters = [];
  if (!ctx) return;
  if (baseTrack) loadTrack(baseTrack);
  for (const z of zones) loadTrack(z.track);
  for (const e of emitters) loadTrack(e.track);
}

/** Replace the list of dynamic proximity ambience sources (e.g. every lit
 *  campfire). Same disc-fade as map audioZones; multiple sources on one track
 *  take the loudest weight. Pass `[]` to silence them. */
export function setAmbientEmitters(list: AmbientEmitter[]): void {
  emitters = list;
  if (!ctx) return;
  for (const e of emitters) loadTrack(e.track);
}

/** Global rain beds + map-ambience duck. Called each frame from updateWeather
 *  (and from weatherClear with zeros). Lazy-loads each rain track only when
 *  that bed's target is non-zero so a drizzle-only stretch never fetches heavy.
 *  light/heavy are 0..1 layer gains (clamped); ambienceDuck 0..1 silences map
 *  base + zones + emitters only (music bus untouched). Rain beds are also
 *  soft-trimmed under music zones inside updateAudio (WEATHER_MUSIC_TRIM). */
export function setWeatherAmbience(opts: {
  light: number;
  heavy: number;
  ambienceDuck?: number;
}): void {
  weatherLight = clamp01(opts.light);
  weatherHeavy = clamp01(opts.heavy);
  ambienceDuck = clamp01(opts.ambienceDuck ?? 0);
  if (!ctx) return;
  if (weatherLight > 0.001) loadTrack(WEATHER_LIGHT);
  if (weatherHeavy > 0.001) loadTrack(WEATHER_HEAVY);
}

/** Map teardown (swapWorld / cinematic stage-out): drop the zone data so every
 *  layer's target hits 0 — updateAudio keeps running through the fade, so the
 *  sound glides out instead of clicking off. Weather rain is left alone (the
 *  next updateWeather frame re-targets it from the live weather state). */
export function resetAudio(): void {
  setAudioMap(null);
}

/** Kick off decode for one-shot SFX so the first playSfx isn't silent. Safe to
 *  call repeatedly — loadTrack no-ops once the buffer is cached. */
export function preloadSfx(...names: string[]): void {
  for (const n of names) loadTrack(n);
}

/** Fire a one-shot SFX from /audio/<name>.{opus,mp3}. No-op while the buffer
 *  is still loading/missing, or if the mixer never booted. volume is a 0..1
 *  trim on top of the master bus (default 1). */
export function playSfx(name: string, volume = 1): void {
  if (!ctx || !sfxGain) return;
  loadTrack(name);
  const buf = buffers.get(name);
  if (!(buf instanceof AudioBuffer)) return;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const v = Math.min(1, Math.max(0, volume));
  if (v < 1) {
    const g = ctx.createGain();
    g.gain.value = v;
    src.connect(g);
    g.connect(sfxGain);
  } else {
    src.connect(sfxGain);
  }
  try { src.start(0); } catch { /* context closed / already started */ }
}

/** Per-frame mix: zone weights from the player position → eased layer gains.
 *  Runs in BOTH frame branches (main + cinematic — the "ambiance stays" list). */
export function updateAudio(dt: number): void {
  if (!ctx || !masterGain) return;
  // bfcache restore can hand back a suspended context after unlock.
  if (unlocked && ctx.state === "suspended") void ctx.resume().catch(() => {});

  masterGain.gain.value = vols.muted ? 0 : vols.master;
  ambienceGain!.gain.value = vols.ambience;
  musicGain!.gain.value = vols.music;

  // Layer targets from the current zones. Before join (or with no map set)
  // map layers stay silent; weather rain still applies (global).
  want.clear();
  // Heavy rain ducks map ambience (field/river/campfire) so weather reads as
  // the outdoor bed. Music zones are exempt from ambienceDuck; rain beds use
  // a soft music-presence trim so town soundtrack still owns the mix.
  const duckMul = 1 - ambienceDuck;
  let musicW = 0;
  const at = anchor ?? (game.joined && game.player ? game.player : null);
  if (at) {
    const px = at.position.x;
    const pz = at.position.z;
    for (const z of zones) {
      const d = Math.hypot(px - z.x, pz - z.z);
      if (d >= z.r) continue;
      // Reversed edge pair (r → inner) = fade 1 inside inner, 0 at r.
      const w = smoothstep(z.r, z.inner, d);
      const key = (z.music ? "m:" : "a:") + z.track;
      const v = w * (z.volume ?? 1) * (z.music ? 1 : duckMul);
      if (v > (want.get(key) ?? 0)) want.set(key, v);
      if (z.music && w > musicW) musicW = w; // duck by presence, not trim
    }
    // Live emitters (campfires, …) — additive ambience only, never music-duck.
    for (const e of emitters) {
      const d = Math.hypot(px - e.x, pz - e.z);
      if (d >= e.r) continue;
      const w = smoothstep(e.r, e.inner, d);
      const key = "a:" + e.track;
      const v = w * (e.volume ?? 1) * duckMul;
      if (v > (want.get(key) ?? 0)) want.set(key, v);
    }
    if (baseTrack) {
      const key = "a:" + baseTrack;
      const base = (1 - musicW) * duckMul; // music owns the soundscape while it's up
      if (base > (want.get(key) ?? 0)) want.set(key, base);
    }
  }

  // Weather rain — global beds under `w:` (no collision with map `a:` keys).
  // Not subject to ambienceDuck; soft-trimmed under music zones.
  const rainMul = 1 - musicW * WEATHER_MUSIC_TRIM;
  if (weatherLight > 0.001) want.set(WEATHER_LIGHT_KEY, weatherLight * rainMul);
  if (weatherHeavy > 0.001) want.set(WEATHER_HEAVY_KEY, weatherHeavy * rainMul);

  // Ensure a layer exists for every wanted key (buffer may still be loading —
  // ensureLayer is cheap and the source starts once decode lands).
  for (const key of want.keys()) ensureLayer(key);

  // Ease every live layer toward its target; start/stop sources at the edges.
  const k = 1 - Math.exp(-dt / FADE_TAU);
  for (const [key, L] of layers) {
    const target = want.get(key) ?? 0;
    L.cur += (target - L.cur) * k;
    if (target > 0.001) {
      L.hold = STOP_HOLD;
      if (!L.src) startLayer(L);
    } else if (L.cur < 0.001) {
      L.cur = 0;
      L.hold -= dt;
      if (L.hold <= 0) {
        stopLayer(L);
        layers.delete(key);
        continue;
      }
    }
    L.gain.gain.value = L.cur;
  }
}

// ---- Internals ------------------------------------------------------------------

/** Containers tried in order — opus first (~half the bytes at ambience
 *  quality), mp3 the fallback. A 404 OR a decode failure (an older Safari on
 *  ogg-opus) falls through to the next, so shipping either file just works. */
const AUDIO_EXTS = ["opus", "mp3"] as const;

function loadTrack(track: string): void {
  if (!ctx || buffers.has(track)) return;
  buffers.set(track, "loading");
  (async () => {
    for (const ext of AUDIO_EXTS) {
      try {
        const res = await fetch(`/audio/${track}.${ext}`);
        if (!res.ok) continue;
        buffers.set(track, await ctx!.decodeAudioData(await res.arrayBuffer()));
        return;
      } catch { /* fetch or decode failed — try the next container */ }
    }
    buffers.set(track, "missing");
    console.warn(`audio: track "${track}" unavailable (assets/audio/${track}.{opus,mp3})`);
  })();
}

function ensureLayer(key: string): void {
  if (!ctx || layers.has(key)) return;
  // `m:` → music bus; `a:` / `w:` → ambience bus (weather uses `w:` so map
  // docs can freely name tracks without colliding with rain beds).
  const music = key.startsWith("m:");
  const track = key.slice(2);
  const gain = ctx.createGain();
  gain.gain.value = 0;
  gain.connect(music ? musicGain! : ambienceGain!);
  layers.set(key, { track, gain, src: null, cur: 0, hold: STOP_HOLD });
  loadTrack(track);
}

/** (Re)start a layer's looped source — buffer sources are ONE-SHOT, so this
 *  always builds a fresh node. No-op while the track is still loading/missing. */
function startLayer(L: Layer): void {
  const buf = buffers.get(L.track);
  if (!(buf instanceof AudioBuffer)) return;
  const src = ctx!.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.connect(L.gain);
  // Random start offset: two zones sharing ambience character shouldn't
  // phase-lock, and re-entering a zone shouldn't always replay the same intro.
  src.start(0, Math.random() * buf.duration);
  L.src = src;
}

function stopLayer(L: Layer): void {
  if (L.src) {
    try { L.src.stop(); } catch { /* already stopped */ }
    L.src.disconnect();
    L.src = null;
  }
  L.gain.disconnect();
}

// ---- Volume controls (O-key panel + character-select Settings view) --------------

/** Build the Master/Music/Ambience sliders + Mute checkbox. Used by BOTH the
 *  in-world O panel and the character-select Settings view; the two share the
 *  module-level `vols` (localStorage-backed), so `refresh()` re-reads the live
 *  values into the inputs when a control comes back into view (e.g. re-opening
 *  the Settings screen after a volume tweak in-world). */
export function makeAudioControls(): { el: HTMLElement; refresh: () => void } {
  const wrap = document.createElement("div");
  const refreshers: Array<() => void> = [];

  const slider = (label: string, get: () => number, set: (v: number) => void) => {
    const row = document.createElement("div");
    row.style.cssText = "display:flex; align-items:center; gap:8px; margin:4px 0;";
    const name = document.createElement("span");
    name.textContent = label;
    name.style.cssText = "width:64px; color:#cfe3cf;";
    const input = document.createElement("input");
    input.type = "range";
    input.min = "0"; input.max = "1"; input.step = "0.05";
    input.value = String(get());
    input.style.cssText = "flex:1; accent-color:#4a9a5a;";
    input.oninput = () => { set(Number(input.value)); saveVols(); };
    refreshers.push(() => { input.value = String(get()); });
    row.append(name, input);
    return row;
  };
  wrap.appendChild(slider("Master", () => vols.master, (v) => { vols.master = v; }));
  wrap.appendChild(slider("Music", () => vols.music, (v) => { vols.music = v; }));
  wrap.appendChild(slider("Ambience", () => vols.ambience, (v) => { vols.ambience = v; }));

  const muteRow = document.createElement("label");
  muteRow.style.cssText = "display:flex; align-items:center; gap:8px; margin-top:4px; cursor:pointer; color:#cfe3cf;";
  const mute = document.createElement("input");
  mute.type = "checkbox";
  mute.checked = vols.muted;
  mute.onchange = () => { vols.muted = mute.checked; saveVols(); };
  refreshers.push(() => { mute.checked = vols.muted; });
  const muteLabel = document.createElement("span");
  muteLabel.textContent = "Mute";
  muteRow.append(mute, muteLabel);
  wrap.appendChild(muteRow);

  return { el: wrap, refresh: () => { for (const fn of refreshers) fn(); } };
}

function appendVolumeSliders(): void {
  const panel = document.getElementById("gfx-panel");
  if (!panel) return;
  const wrap = document.createElement("div");
  wrap.style.cssText = "margin-top:10px; border-top:1px solid #3a4a3a; padding-top:10px;";

  const title = document.createElement("div");
  title.textContent = "Audio";
  title.style.cssText = "margin-bottom:6px; color:#9fd3ff; letter-spacing:.03em;";
  wrap.appendChild(title);

  wrap.appendChild(makeAudioControls().el);
  panel.appendChild(wrap);
}
